/**
 * Named locations — query + mutation hooks.
 *
 * Mounted at /api/v1/tenants/{tenant_id}/conditional-access/
 * named-locations via the WithPathTenant wrapper in routes.go.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  namedLocationSchema,
  namedLocationsListResponseSchema,
  type CreateNamedLocationInput,
  type NamedLocation,
  type NamedLocationsListResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/conditional-access/named-locations`;
}

export async function fetchNamedLocations(
  tenantID: string,
): Promise<NamedLocationsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return namedLocationsListResponseSchema.parse(raw);
}

export function useNamedLocationsQuery(tenantID: string) {
  return useQuery<NamedLocationsListResponse, ApiError>({
    queryKey: queryKeys.namedLocations.list(tenantID),
    queryFn: () => fetchNamedLocations(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function useCreateNamedLocationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<NamedLocation, ApiError, CreateNamedLocationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => namedLocationSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.namedLocations.all(tenantID) });
    },
  });
}

/** The update payload is the create payload plus the required
 * `expected_version` for optimistic concurrency. The backend compares
 * this against the current row's `version` and returns 409 if they
 * differ, so a stale editor cannot silently clobber another admin's
 * concurrent edit. */
export type UpdateNamedLocationInput = CreateNamedLocationInput & {
  expected_version: number;
};

export function useUpdateNamedLocationMutation(tenantID: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation<NamedLocation, ApiError, UpdateNamedLocationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(locationId)}`, {
          method: "PUT",
          body: input,
        }).then((raw) => namedLocationSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(
        queryKeys.namedLocations.detail(tenantID, locationId),
        updated,
      );
      qc.invalidateQueries({ queryKey: queryKeys.namedLocations.all(tenantID) });
    },
  });
}

export function useDeleteNamedLocationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (locationId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(locationId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.namedLocations.all(tenantID) });
    },
  });
}
